import { ActionFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Utils
import { compressSessionStorage } from '../../../../utils/sessionStorage';
import { claimDraftScope } from '../../../../utils/draftOwnership';
import {
  COMMUNICATION_CREATION_DRAFT_KEYS,
  COMMUNICATION_CREATION_SCOPE,
  communicationCreationOwnerId,
} from '../../../../utils/draftScopes';

// Constants
import { formNaming } from '../../../../components/forms/NewCommunicationForm/constants';

/**
 * La puerta que EMPIEZA el alta de una comunicación («Nueva comunicación» de
 * la OT o de la campaña, `ShowOT` → `pass-data`).
 *
 * 🔴 El dueño (`parent:<:id>`) se reclama AQUÍ, antes de escribir (K3 de la
 * batería QA del navegador, 2026-11-03; spec 2026-11-02 wizard-draft-ownership):
 * el loader que corre después de esta acción reclama el mismo id y, si el dueño
 * fuera todavía el del alta anterior, descartaría el borrador recién escrito.
 *
 * Y el borrador nuevo SUSTITUYE al anterior: antes se FUSIONABA
 * `{...borrador anterior, parentOrderId: nuevo}`, así que el alta de otra OT a
 * medias heredaba su descripción y sus campos. Hoy no se veía porque `ShowOT`
 * borra la copia antes de llegar aquí; la puerta deja de depender de eso.
 *
 * La OT/campaña elegida en Redux (`selectedRowModalTable`) la pone `ShowOT`
 * antes de enviar: no se toca aquí.
 */
export const ActionNewCommunication: ActionFunction = async ({ request, params }) => {
  const formData = await request.formData();
  const intent = formData.get('intent');

  if (intent === 'pass-data') {
    try {
      // El padre es el de la URL; el `id` del formulario es el mismo (`ShowOT`
      // manda el de la OT que pinta) y queda de reserva.
      const formParentId = formData.get('id');
      const parentOrderId =
        params.id ?? (typeof formParentId === 'string' ? formParentId : undefined);
      const parentOrderRoid = formData.get('roid');

      // Store navigation context for ShowOT flow
      // This marks that we're coming from a work order, so after creation
      // we should return to the parent order, not the communications section
      sessionStorage.setItem(
        formNaming.COMMUNICATION_NAVIGATION_CONTEXT,
        compressSessionStorage(
          JSON.stringify({
            origin: 'workorder',
            parentOrderId,
            parentOrderRoid,
          })
        )
      );

      if (parentOrderId) {
        claimDraftScope(COMMUNICATION_CREATION_SCOPE, communicationCreationOwnerId(parentOrderId));
      }
      // Alta NUEVA: aunque el dueño ya fuera este padre (un alta suya a
      // medias), se empieza de cero — también lo elegido a mano en el modal.
      COMMUNICATION_CREATION_DRAFT_KEYS.forEach((key) => sessionStorage.removeItem(key));
      sessionStorage.setItem(
        formNaming.NEW_COMMUNICATION_FORM,
        compressSessionStorage(JSON.stringify({ parentOrderId, parentOrderRoid }))
      );

      return { parentOrderId, parentOrderRoid };
    } catch (err) {
      Sentry.captureException(err);
      console.log('> NewCommunication pass-data - error:', err);
    }
  }

  return null;
};
